import {
  Box,
  Divider,
  Flex,
  Spinner,
  Td,
  Text,
  Tr,
  useToast,
} from '@chakra-ui/react';
import { getIn } from 'formik';
import React, { useState } from 'react';

import {
  finleyAssertUnreachable,
  utils as commonUtils,
} from '@finley-technologies/common';
import { CreditAgreement, Reporting } from '@finley-technologies/finley-types';
import { validate as reportingValidate } from '@finley-technologies/reporting-utilities';

import { ReportingGenerateWorkleaseModal } from './ReportingGeneratingWorkleaseModal';
import {
  createReportingInputShell,
  ReportingInputConfig,
} from './ReportingInputConfig';
import {
  createReportingDocxOutputConfigShell,
  createReportingXlsxOutputConfigShell,
  ReportingOutputConfig,
} from './ReportingOutputConfig';
import {
  DeleteIconButton,
  ResetIconButton,
  SaveIconButton,
  UploadIconButton,
} from 'packages/common-frontend/components/IconButton';
import {
  formatSelectOptions,
  Select,
} from 'packages/common-frontend/components/Select';
import { CreditAgreementFullArtifactWipConfigType } from 'packages/creditAgreements/ui/types';
import {
  useGenerateReportingOutputXlsxFileMutation,
  useProduceReportingGenerateMessageMutation,
} from 'packages/reporting/requests/reporting.gql.generated';

export const createReportingXlsxConfigShell = (): CreditAgreement.Config.Reporting.XlsxConfig => {
  return {
    type: 'xlsx',
    input: createReportingInputShell(),
    output: createReportingXlsxOutputConfigShell(),
  };
};

export const ReportingPropertyConfig: React.FC<{
  reportingProperty: CreditAgreement.DomainEntity.ReportingProperty;
  wipConfig?: CreditAgreementFullArtifactWipConfigType;
  configPropertyKey: string;
  columnLength: number;
}> = ({ reportingProperty, wipConfig, configPropertyKey, columnLength }) => {
  const { name: reportName, _config_, reportConfigTypes } = reportingProperty;

  const toast = useToast();
  const [queryResults, setQueryResults] = useState<
    CreditAgreement.Config.QueryResult[]
  >([]);
  const [
    showProduceGenerateModal,
    setShowProduceGenerateModal,
  ] = useState<boolean>(false);
  const [
    showDevGenerateReportModal,
    setShowDevGenerateReportModal,
  ] = useState<boolean>(false);

  const [
    produceGenerateMessage,
    { loading: isProduceGenerateMessageLoading },
  ] = useProduceReportingGenerateMessageMutation({
    onCompleted: ({ result }) => {
      switch (result.status) {
        case 'success':
          toast({
            title: 'Produce Report',
            description: 'Successfully produced message to generate report',
            status: 'info',
            position: 'top',
            duration: 5000,
            isClosable: true,
          });
          break;
        case 'error':
          toast({
            title: 'Produce Report',
            description:
              'Failed to produce message to generate report. Please contact Finley support',
            status: 'error',
            position: 'top',
            duration: 8000,
            isClosable: true,
          });
          break;
        default:
          finleyAssertUnreachable(result.status);
      }
    },
  });

  const [
    generateDevReport,
    { loading: isGenerateDevReportLoading },
  ] = useGenerateReportingOutputXlsxFileMutation({
    onCompleted: ({ result }) => {
      switch (result.status) {
        case 'success':
          toast({
            title: '[DEV] Generate Report',
            description: 'Successfully generated report',
            status: 'info',
            position: 'top',
            duration: 5000,
            isClosable: true,
          });
          break;
        case 'error':
          toast({
            title: '[DEV] Generate Report',
            description: 'Failed to generate report. Please check logs',
            status: 'error',
            position: 'top',
            duration: 8000,
            isClosable: true,
          });
          break;
        default:
          finleyAssertUnreachable(result.status);
      }
    },
  });

  return (
    <Tr>
      <Td colSpan={columnLength}>
        <Box mt="2">
          <Flex alignItems="center">
            <Text as="h2" fontSize="2xl" fontWeight="bold" mr="2">
              Config
            </Text>
            {wipConfig?.isMutateAllowed(configPropertyKey) && (
              <>
                <SaveIconButton
                  ariaLabel="save config"
                  label="Save Config"
                  onClick={() => {
                    const { values } = wipConfig.formProps;
                    const reportingConfig = getIn(values, configPropertyKey);
                    const configErrors = reportingValidate.validateReportingConfig(
                      { config: reportingConfig },
                    );
                    if (configErrors.length > 0) {
                      toast({
                        title: 'Reporting Config Errors',
                        description: configErrors.join(', '),
                        status: 'error',
                        position: 'top',
                        duration: 10000,
                        isClosable: true,
                      });
                      return;
                    }

                    wipConfig.saveCreditAgreementFullArtifactWip({
                      variables: {
                        creditAgreementFullArtifactWip: {
                          id: values.id,
                          creditFacilityId: values.creditFacilityId,
                          creditAgreement: JSON.stringify(values),
                        },
                      },
                    });
                  }}
                />

                {isProduceGenerateMessageLoading ? (
                  <Spinner size="sm" mx="2" p="2" />
                ) : (
                  <UploadIconButton
                    ariaLabel="produce report"
                    label="Produce Report"
                    onClick={() => {
                      setShowProduceGenerateModal(true);
                    }}
                  />
                )}

                {/*
                 * Only exposing this QA tool on Dev since don't want to lock
                 * up FinleyCore when generating reports
                 */}
                {commonUtils.EnvUtils.isDevelopment() &&
                  (isGenerateDevReportLoading ? (
                    <Spinner size="sm" mx="2" p="2" />
                  ) : (
                    <ResetIconButton
                      ariaLabel="qa report"
                      label="QA Report (Dev)"
                      onClick={() => {
                        setShowDevGenerateReportModal(true);
                      }}
                    />
                  ))}

                <DeleteIconButton
                  ariaLabel="reset config"
                  label="Reset Configuration"
                  onClick={() => {
                    wipConfig.formProps.setFieldValue(
                      configPropertyKey,
                      createReportingXlsxConfigShell(),
                    );
                  }}
                />
              </>
            )}
          </Flex>

          {wipConfig?.isMutateAllowed(configPropertyKey) ? (
            <>
              <Divider my="6" />

              <Flex alignItems="center">
                <Text mr="3">Report Type:</Text>
                <Box w="40">
                  <Select<CreditAgreement.Config.Reporting.ReportTemplateFileType>
                    value={_config_.type}
                    options={formatSelectOptions(
                      CreditAgreement.Config.Reporting
                        .ReportTemplateFileTypeIterable,
                    )}
                    onChange={(type) => {
                      if (type === _config_.type || !type) {
                        return;
                      }

                      let newConfig: CreditAgreement.Config.Reporting.Config;

                      switch (type) {
                        case 'docx':
                          newConfig = {
                            type,
                            input: _config_.input,
                            output: createReportingDocxOutputConfigShell(),
                          };
                          break;
                        case 'xlsx':
                          newConfig = {
                            type,
                            input: _config_.input,
                            output: createReportingXlsxOutputConfigShell(),
                          };
                          break;
                        default:
                          throw finleyAssertUnreachable(type);
                      }

                      wipConfig.formProps.setFieldValue(
                        configPropertyKey,
                        newConfig,
                      );
                    }}
                  />
                </Box>
              </Flex>
            </>
          ) : null}

          <Divider my="6" />

          <ReportingInputConfig
            inputConfigPropertyKey={`${configPropertyKey}.input`}
            wipConfig={wipConfig}
            reportingProperty={reportingProperty}
            queryResults={queryResults}
            setQueryResults={setQueryResults}
          />

          <Divider my="8" />

          <ReportingOutputConfig
            wipConfig={wipConfig}
            config={_config_}
            reportConfigTypes={reportConfigTypes}
            configPropertyKey={configPropertyKey}
            queryResults={queryResults}
          />
        </Box>
      </Td>

      <ReportingGenerateWorkleaseModal
        title="Send Worklease to Produce Report"
        isOpen={showProduceGenerateModal}
        onCancel={() => {
          setShowProduceGenerateModal(false);
        }}
        onExecute={(worklease: Reporting.ReportingGenerateWorklease) => {
          produceGenerateMessage({ variables: { worklease } });
        }}
        reportConfigTypes={reportConfigTypes}
        creditAgreementId={wipConfig?.formProps.values.id ?? null}
        creditFacilityId={wipConfig?.formProps.values.creditFacilityId ?? null}
        reportName={reportName}
        reportTemplateFileType={_config_.type}
      />

      <ReportingGenerateWorkleaseModal
        title="Generate Dev Report"
        isOpen={showDevGenerateReportModal}
        onCancel={() => {
          setShowDevGenerateReportModal(false);
        }}
        onExecute={(worklease: Reporting.ReportingGenerateWorklease) => {
          generateDevReport({ variables: { worklease } });
        }}
        creditAgreementId={wipConfig?.formProps.values.id ?? null}
        creditFacilityId={wipConfig?.formProps.values.creditFacilityId ?? null}
        reportConfigTypes={reportConfigTypes}
        reportName={reportName}
        reportTemplateFileType={_config_.type}
      />
    </Tr>
  );
};
